import { BorderBeam } from "@/components/magicui/border-beam";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ChartBarIncreasingIcon,
  Database,
  Fingerprint,
  IdCard,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";

export default function Features({ featuresRef }) {
  const [activeItem, setActiveItem] = useState("item-1");

  const imgs = {
    "item-1": {
      img: "goodquestion_dark.png",
      alt: "Vast and up-to-date question bank visualization",
    },
    "item-2": {
      img: "question_light.png",
      alt: "Realistic exam simulation interface",
    },
    "item-3": {
      img: "studywrong_light.png",
      alt: "Specialized B2 avionics module content",
    },
    "item-4": {
      img: "result_light.png",
      alt: "Personalized performance analytics dashboard",
    },
  };

  return (
    <section className="py-12 md:py-20 lg:py-32" ref={featuresRef}>
      <div className="bg-linear-to-b absolute inset-0 -z-10 sm:inset-6 sm:rounded-b-3xl dark:block dark:to-[color-mix(in_oklab,var(--color-zinc-900)_75%,var(--color-background))]"></div>
      <div className="mx-auto max-w-5xl space-y-8 px-6 md:space-y-16 lg:space-y-20 dark:[--color-border:color-mix(in_oklab,var(--color-white)_10%,transparent)]">
        <div className="relative z-10 mx-auto max-w-2xl space-y-6 text-center">
          <h2 className="text-balance text-4xl font-semibold lg:text-6xl">
            Study Smart, Not Hard.
          </h2>
          <p>
            Stop guessing. Pinpoint your weak spots with advanced analytics and
            transform your preparation. Watch your scores climb and pass with
            confidence.
          </p>
        </div>

        <div className="grid gap-12 sm:px-12 md:grid-cols-2 lg:gap-20 lg:px-0">
          <Accordion
            type="single"
            value={activeItem}
            onValueChange={(value) => setActiveItem(value)}
            className="w-full"
          >
            <AccordionItem value="item-1">
              <AccordionTrigger>
                <div className="flex items-center gap-2 text-base cursor-pointer">
                  <Database className="size-4" />
                  Expert-Authored Question Bank
                </div>
              </AccordionTrigger>
              <AccordionContent>
                Master the material with thousands of questions, precisely
                authored to reflect the latest EASA syllabus, including the 2025
                legislative updates. Our bank is continuously expanding to keep
                you ahead.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>
                <div className="flex items-center gap-2 text-base cursor-pointer">
                  <Fingerprint className="size-4" />
                  Authentic Exam Experience
                </div>
              </AccordionTrigger>
              <AccordionContent>
                Feel the pressure of the real exam. Our timed mock tests
                perfectly simulate the EASA Part-66 environment, building your
                confidence and time-management skills for exam day.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>
                <div className="flex items-center gap-2 text-base cursor-pointer">
                  <IdCard className="size-4" />
                  Dedicated B2 Avionics Focus
                </div>
              </AccordionTrigger>
              <AccordionContent>
                We understand the unique challenges of the B2 stream. Our
                content includes dedicated, in-depth questions specifically
                tailored to help you master complex avionics modules.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4">
              <AccordionTrigger>
                <div className="flex items-center gap-2 text-base cursor-pointer">
                  <ChartBarIncreasingIcon className="size-4" />
                  Intelligent Performance Analytics
                </div>
              </AccordionTrigger>
              <AccordionContent>
                Instantly get a detailed breakdown of your performance. Identify
                your weakest sub-modules, track your progress over time, and
                focus your study efforts where they matter most.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="bg-background relative flex overflow-hidden rounded-3xl border p-2">
            <div className="w-15 absolute inset-0 right-0 ml-auto border-l bg-[repeating-linear-gradient(-45deg,var(--color-border),var(--color-border)_1px,transparent_1px,transparent_8px)]"></div>
            <div className="bg-background relative w-300 rounded-2xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeItem}-id`}
                  initial={{ opacity: 0, y: 6, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.98 }}
                  transition={{ duration: 0.2 }}
                  className="size-full overflow-hidden rounded-2xl border shadow-md"
                >
                  <img
                    src={imgs[activeItem].img}
                    className="size-full object-cover object-left-top dark:mix-blend-lighten"
                    alt={imgs[activeItem].alt}
                    width={1207}
                    height="auto"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
            <BorderBeam
              duration={10}
              size={500}
              className="from-transparent via-blue-600 to-transparent dark:via-white/50"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
